import React from "react";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import { QuantityControlProps } from "./QuantityControl.types";

const QuantityControl: React.FC<QuantityControlProps> = ({
  quantity,
  onQuantityChange,
  min = 1,
  max = 99,
  disabled = false,
  "aria-label": ariaLabel = "Quantity control",
  size = "medium",
}) => {
  const theme = useTheme();

  const handleDecrease = () => {
    if (quantity > min && !disabled) {
      onQuantityChange(quantity - 1);
    }
  };

  const handleIncrease = () => {
    if (quantity < max && !disabled) {
      onQuantityChange(quantity + 1);
    }
  };

  const isDecreaseDisabled = disabled || quantity <= min;
  const isIncreaseDisabled = disabled || quantity >= max;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        backgroundColor: theme.palette.action.hover,
        borderRadius: 3,
        height: size === "small" ? 32 : 40,
        width: size === "small" ? 80 : 100,
        padding: "3px",
        gap: 0.5,
      }}
      role="group"
      aria-label={ariaLabel}
    >
      <IconButton
        onClick={handleDecrease}
        disabled={isDecreaseDisabled}
        size={size === "small" ? "small" : "medium"}
        aria-label="Decrease quantity"
        sx={{
          minWidth: size === "small" ? 20 : 24,
          width: size === "small" ? 20 : 24,
          height: size === "small" ? 20 : 24,
          borderRadius: "50%",
          color: theme.palette.text.primary,
          opacity: isDecreaseDisabled ? 0.6 : 1,
          "&:hover": {
            backgroundColor: theme.palette.action.selected,
          },
        }}
      >
        <Remove sx={{ fontSize: size === "small" ? 12 : 16 }} />
      </IconButton>

      <Typography
        variant={size === "small" ? "caption" : "body2"}
        sx={{
          fontWeight: 500,
          color: theme.palette.text.primary,
          fontSize: size === "small" ? "14px" : "16px",
          textAlign: "center",
          minWidth: size === "small" ? 20 : 24,
          userSelect: "none",
        }}
        role="status"
        aria-live="polite"
        aria-label={`Quantity: ${quantity}`}
      >
        {quantity}
      </Typography>

      <IconButton
        onClick={handleIncrease}
        disabled={isIncreaseDisabled}
        size={size === "small" ? "small" : "medium"}
        aria-label="Increase quantity"
        sx={{
          minWidth: size === "small" ? 20 : 24,
          width: size === "small" ? 20 : 24,
          height: size === "small" ? 20 : 24,
          borderRadius: "50%",
          color: theme.palette.text.primary,
          opacity: isIncreaseDisabled ? 0.6 : 1,
          "&:hover": {
            backgroundColor: theme.palette.action.selected,
          },
        }}
      >
        <Add sx={{ fontSize: size === "small" ? 12 : 16 }} />
      </IconButton>
    </Box>
  );
};

export default QuantityControl;
